/**
 * Kubectl Console WebSocket Service
 *
 * 管理 kubectl console 的 WebSocket 连接，提供命令执行、自动完成和取消功能
 */

class KubectlWebSocketService {
  constructor() {
    // WebSocket 实例
    this.ws = null;

    // 连接状态
    this.connected = false;
    this.connecting = false;

    // 消息处理器映射 (sessionId -> handlers)
    this.handlers = new Map();

    // 待处理的消息队列（连接建立前发送的消息）
    this.messageQueue = [];

    // 重连相关（无限重连，指数退避，最大延迟 30 秒）
    this.reconnectAttempts = 0;
    this.reconnectDelay = 1000;
    this.reconnectTimer = null;

    // 事件监听器
    this.onConnectionChange = null;
    this.onError = null;

    // Token 获取函数（可在运行时设置）
    this.getToken = () => {
      // 默认从 localStorage 获取 token
      return localStorage.getItem('token');
    };
  }

  /**
   * 设置 Token 获取函数
   * @param {Function} fn - 返回 token 的函数
   */
  setTokenGetter(fn) {
    this.getToken = fn;
  }

  /**
   * 连接 WebSocket 服务器
   */
  connect() {
    if (this.connected || this.connecting) {
      return;
    }

    this.connecting = true;

    // 获取环境配置
    const envType = process.env['REACT_APP_ENV'];
    const rawUrl = process.env[envType]?.url1?.replace(/^https?:\/\//, '');

    if (!rawUrl) {
      this.connecting = false;
      this.handleError(new Error('WebSocket 地址未配置'));
      // 连接失败，触发重连
      this.attemptReconnect();
      return;
    }

    const token = this.getToken();
    if (!token) {
      this.connecting = false;
      this.handleError(new Error('Token 未找到'));
      // Token 缺失也触发重连（可能正在刷新）
      this.attemptReconnect();
      return;
    }

    // 构建 WebSocket URL: wss://{host}/ws/kubectl?token={token}
    const wsUrl = `wss://${rawUrl}/ws/kubectl?token=${encodeURIComponent(token)}`;
    //const wsUrl = `ws://localhost:8080/management-portal-stg-api/ws/kubectl?token=${encodeURIComponent(token)}`;

    try {
      this.ws = new WebSocket(wsUrl);
      this.setupEventHandlers();
    } catch (error) {
      this.connecting = false;
      this.handleError(error);
      // 连接失败，触发重连
      this.attemptReconnect();
    }
  }

  /**
   * 设置 WebSocket 事件处理器
   */
  setupEventHandlers() {
    this.ws.onopen = () => {
      this.connected = true;
      this.connecting = false;
      this.reconnectAttempts = 0;

      // 发送队列中的消息
      this.flushMessageQueue();

      // 通知连接状态变化
      if (this.onConnectionChange) {
        this.onConnectionChange(true);
      }
    };

    this.ws.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data);
        this.handleMessage(message);
      } catch (error) {
        // Failed to parse message
      }
    };

    this.ws.onerror = (error) => {
      this.handleError(error);
    };

    this.ws.onclose = (event) => {
      this.connected = false;
      this.connecting = false;
      this.ws = null;

      // 通知连接状态变化
      if (this.onConnectionChange) {
        this.onConnectionChange(false);
      }

      // 尝试重连
      this.attemptReconnect();
    };
  }

  /**
   * 处理接收到的消息
   */
  handleMessage(message) {
    const { type, sessionId, data } = message;

    // 查找对应的处理器
    const handlers = this.handlers.get(sessionId);
    if (!handlers) {
      return;
    }

    // 根据消息类型调用对应的处理器
    switch (type) {
      case 'status':
        if (handlers.onStatus) {
          handlers.onStatus(data);
        }
        break;

      case 'output':
        if (handlers.onOutput) {
          handlers.onOutput(data);
        }
        break;

      case 'error':
        if (handlers.onError) {
          handlers.onError(data);
        }
        break;

      case 'autocomplete':
        if (handlers.onAutocomplete) {
          handlers.onAutocomplete(data);
        }
        break;

      default:
        // Unknown message type
    }
  }

  /**
   * 发送消息到服务器
   */
  sendMessage(message) {
    if (!this.connected) {
      // 如果未连接，将消息加入队列
      this.messageQueue.push(message);
      return;
    }

    try {
      const json = JSON.stringify(message);
      this.ws.send(json);
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * 发送队列中的消息
   */
  flushMessageQueue() {
    while (this.messageQueue.length > 0) {
      const message = this.messageQueue.shift();
      this.sendMessage(message);
    }
  }

  /**
   * 执行 kubectl 命令
   * @param {string} cluster - 集群名称
   * @param {string} namespace - 命名空间
   * @param {string} command - kubectl 命令
   * @param {Object} handlers - 事件处理器
   * @returns {string} sessionId
   */
  executeCommand(cluster, namespace, command, handlers = {}) {
    const sessionId = `cmd-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    // 注册处理器
    this.handlers.set(sessionId, {
      onStatus: handlers.onStatus,
      onOutput: handlers.onOutput,
      onError: handlers.onError,
      onAutocomplete: handlers.onAutocomplete,
    });

    // 发送命令
    this.sendMessage({
      type: 'command',
      sessionId,
      data: {
        cluster,
        namespace,
        command,
      },
    });

    return sessionId;
  }

  /**
   * 取消正在执行的命令
   * @param {string} sessionId - 会话 ID
   */
  cancelCommand(sessionId) {
    this.sendMessage({
      type: 'cancel',
      sessionId,
    });
  }

  /**
   * 获取自动完成建议
   * @param {string} input - 当前输入
   * @param {number} cursor - 光标位置
   * @param {string} cluster - 集群名称
   * @param {string} namespace - 命名空间
   * @param {Function} callback - 回调函数
   * @returns {string} sessionId
   */
  getAutocomplete(input, cursor, cluster, namespace, callback) {
    const sessionId = `auto-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    // 注册处理器
    this.handlers.set(sessionId, {
      onAutocomplete: callback,
    });

    // 发送请求
    this.sendMessage({
      type: 'autocomplete',
      sessionId,
      data: {
        input,
        cursor,
        cluster,
        namespace,
      },
    });

    return sessionId;
  }

  /**
   * 移除处理器
   * @param {string} sessionId - 会话 ID
   */
  removeHandlers(sessionId) {
    this.handlers.delete(sessionId);
  }

  /**
   * 尝试重连
   * 使用指数退避策略，最大延迟不超过 30 秒
   */
  attemptReconnect() {
    // 计算重连延迟（指数退避，最大 30 秒）
    this.reconnectAttempts++;
    const maxDelay = 30000;
    const delay = Math.min(this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1), maxDelay);

    this.reconnectTimer = setTimeout(() => {
      this.connect();
    }, delay);
  }

  /**
   * 处理错误
   */
  handleError(error) {
    if (this.onError) {
      this.onError(error);
    }
  }

  /**
   * 断开连接
   */
  disconnect() {

    // 清除重连定时器
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    // 关闭 WebSocket
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }

    this.connected = false;
    this.connecting = false;
    this.reconnectAttempts = 0;

    // 清空消息队列
    this.messageQueue = [];

    // 清空处理器
    this.handlers.clear();
  }

  /**
   * 设置连接状态变化监听器
   */
  onConnectionStatusChange(callback) {
    this.onConnectionChange = callback;
  }

  /**
   * 设置错误监听器
   */
  setErrorHandler(callback) {
    this.onError = callback;
  }

  /**
   * 获取 WebSocket 实例（用于兼容性）
   */
  getInstance() {
    return this.ws;
  }
}

// 导出单例
const kubectlWebSocketService = new KubectlWebSocketService();

export default kubectlWebSocketService;
